import type { IParameter } from '../../create-api-context';
import type { ICreateApiActionPayload } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { jsonToParameters } from './json-to-parameters';

const SUPPORTED_METHODS = new Set(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']);
const MAX_NAME_LENGTH = 64;

const SKIP_HEADERS = new Set([
  'authorization',
  'host',
  'content-length',
  'content-type',
  'accept-encoding',
  'content-encoding',
  'connection',
  'transfer-encoding',
]);

const SILENT_FLAGS = new Set([
  '-s',
  '-S',
  '--silent',
  '--show-error',
  '-f',
  '--fail',
  '-i',
  '--include',
  '-N',
  '--no-buffer',
  '-#',
  '--progress-bar',
  '--fail-with-body',
]);

const WARN_FLAGS: Record<string, string> = {
  '-k': 'Ignored --insecure. TLS verification is controlled by the tool, not the action.',
  '--insecure': 'Ignored --insecure. TLS verification is controlled by the tool, not the action.',
  '-L': 'Ignored --location (follow redirects).',
  '--location': 'Ignored --location (follow redirects).',
  '--location-trusted': 'Ignored --location-trusted.',
  '-v': 'Ignored --verbose.',
  '--verbose': 'Ignored --verbose.',
  '--compressed': 'Ignored --compressed.',
  '-x': 'Ignored proxy flag.',
  '--proxy': 'Ignored proxy flag.',
  '--cert': 'Ignored client certificate flag.',
  '--key': 'Ignored client key flag.',
  '--cacert': 'Ignored CA certificate flag.',
  '-m': 'Ignored timeout flag.',
  '--max-time': 'Ignored timeout flag.',
  '--connect-timeout': 'Ignored timeout flag.',
  '--retry': 'Ignored --retry.',
  '--http2': 'Ignored HTTP version flag.',
  '--http1.1': 'Ignored HTTP version flag.',
  '--http2-prior-knowledge': 'Ignored HTTP version flag.',
  '-o': 'Ignored --output.',
  '--output': 'Ignored --output.',
  '-O': 'Ignored --remote-name.',
  '--remote-name': 'Ignored --remote-name.',
  '-I': 'HEAD (-I) is not a supported method.',
  '--head': 'HEAD is not a supported method.',
  '--unix-socket': 'Ignored --unix-socket.',
  '-T': 'Ignored --upload-file.',
  '--upload-file': 'Ignored --upload-file.',
};

const VALUE_FLAGS = new Set([
  '-X',
  '--request',
  '-H',
  '--header',
  '-d',
  '--data',
  '--data-raw',
  '--data-binary',
  '--data-ascii',
  '--data-urlencode',
  '--json',
  '-F',
  '--form',
  '--form-string',
  '-b',
  '--cookie',
  '-A',
  '--user-agent',
  '-u',
  '--user',
  '-x',
  '--proxy',
  '-o',
  '--output',
  '-m',
  '--max-time',
  '--connect-timeout',
  '--url',
  '--cert',
  '--key',
  '--cacert',
  '--capath',
  '-E',
  '-T',
  '--upload-file',
  '--unix-socket',
  '--retry',
  '-e',
  '--referer',
  '--max-redirs',
  '-w',
  '--write-out',
]);

export type ParseCurlOptions = {
  baseUrl?: string;
};

export type ParseCurlResult = {
  payload: ICreateApiActionPayload;
  warnings: string[];
};

class CurlParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CurlParseError';
  }
}

export function parseCurlToApiAction(
  rawCurl: string,
  options: ParseCurlOptions = {},
): ParseCurlResult {
  const warnings: string[] = [];
  const tokens = tokenize(normalizeCurl(rawCurl));

  if (tokens.length === 0) {
    throw new CurlParseError('Paste a cURL command to import.');
  }

  const parsed = parseTokens(tokens, warnings);
  if (!parsed.url) {
    throw new CurlParseError('cURL command is missing a URL.');
  }

  const method = resolveMethod(parsed);

  rejectGraphqlCurl(parsed, method);

  if (!SUPPORTED_METHODS.has(method)) {
    throw new CurlParseError(
      `Method ${method} is not supported. Use GET, POST, PUT, DELETE, or PATCH.`,
    );
  }

  let requestUrl: URL;
  try {
    requestUrl = new URL(ensureAbsoluteUrl(parsed.url, options.baseUrl));
  } catch {
    throw new CurlParseError('cURL URL is invalid.');
  }

  const relativized = relativizePath(requestUrl, options.baseUrl);
  if (relativized.warning) warnings.push(relativized.warning);

  const templated = extractPathParams(relativized.path);
  const inputParameters: Record<string, IParameter> = {};

  const addParam = (param: IParameter) => {
    const key = uniqueKey(inputParameters, param.Name || '');
    if (key !== param.Name) {
      warnings.push(`Renamed duplicate parameter "${param.Name}" to "${key}".`);
      param.Name = key;
    }
    inputParameters[key] = param;
  };

  for (const name of templated.params) {
    addParam(scalarParam(name, 'path', 'string', undefined, true));
  }

  requestUrl.searchParams.forEach((value, name) => {
    const inferred = inferScalar(value);
    addParam(scalarParam(name, 'query', inferred.type, inferred.value, true));
  });

  for (const cookie of parsed.cookies) {
    addParam(scalarParam(cookie.name, 'cookie', 'string', cookie.value, false));
  }

  for (const header of parsed.headers) {
    const lower = header.name.toLowerCase();
    if (lower === 'cookie') {
      parseCookieString(header.value).forEach((cookie) => {
        addParam(scalarParam(cookie.name, 'cookie', 'string', cookie.value, false));
      });
      continue;
    }
    if (lower === 'authorization') {
      warnings.push('Skipped Authorization header. Configure auth on the Authentication tab.');
      continue;
    }
    if (SKIP_HEADERS.has(lower)) continue;
    addParam(scalarParam(header.name, 'header', 'string', header.value, false));
  }

  if (parsed.user) {
    warnings.push('Skipped -u/--user. Configure auth on the Authentication tab.');
  }

  const contentType = parsed.contentType.toLowerCase();
  const bodyKind = resolveBodyKind(parsed, contentType);

  if (parsed.forceGet && parsed.bodies.length > 0) {
    const joined = parsed.bodies.join('&');
    appendUrlEncoded(joined, 'query', addParam);
  } else if (bodyKind === 'json') {
    const rawBody = parsed.jsonBody ?? parsed.bodies.join('');
    addJsonBody(rawBody, addParam, warnings);
  } else if (bodyKind === 'form') {
    if (parsed.formFields.length > 0) {
      parsed.formFields.forEach((field) => {
        if (field.skippedFile) {
          warnings.push(`Skipped file field "${field.name}" (@file is not available in the browser).`);
          return;
        }
        const inferred = inferScalar(field.value);
        addParam(scalarParam(field.name, 'form', inferred.type, inferred.value, true));
      });
    }
    if (parsed.bodies.length > 0) {
      appendUrlEncoded(parsed.bodies.join('&'), 'form', addParam);
    }
  } else if (bodyKind === 'raw') {
    const rawBody = parsed.bodies.join('');
    if (rawBody.startsWith('@')) {
      warnings.push('Skipped file body (@file is not available in the browser).');
    } else if (rawBody) {
      addParam({
        Name: 'body',
        Type: 'string',
        Description: '',
        Required: true,
        Location: 'raw_body',
        DefaultValue: rawBody,
        ContentType: parsed.contentType || 'text/plain',
      });
    }
  }

  const name = deriveName(templated.path);
  const endpoint = templated.path.startsWith('/') ? templated.path : `/${templated.path}`;

  return {
    payload: {
      name,
      description: 'Imported from cURL',
      method,
      endpoint,
      input_parameters: inputParameters,
      output_parameters: {},
    },
    warnings: unique(warnings),
  };
}

/**
 * Reject a GraphQL request pasted into cURL import (a POST whose JSON body
 * carries a GraphQL `query`) by throwing — GraphQL needs its schema, so it must
 * be added via "Import from GraphQL". REST requests pass through untouched.
 */
function rejectGraphqlCurl(parsed: ParsedCurl, method: string): void {
  if (method !== 'POST' || parsed.forceGet) return;

  const rawBody = parsed.jsonBody ?? (parsed.bodies.length > 0 ? parsed.bodies.join('') : '');
  if (!rawBody.trim()) return;

  let body: unknown;
  try {
    body = JSON.parse(rawBody);
  } catch {
    return;
  }

  if (!body || typeof body !== 'object' || Array.isArray(body)) return;
  const query = (body as Record<string, unknown>).query;
  if (typeof query !== 'string' || !query.trim() || !looksLikeGraphQLQuery(query)) return;

  throw new CurlParseError(
    'This looks like a GraphQL request. Use “Import from GraphQL” to upload the schema — cURL import is for REST APIs only.',
  );
}

/** True when a JSON `query` string actually looks like a GraphQL operation
 * (named op, or an anonymous `{ … }`) — so a REST body that merely has a
 * string field named `query` isn't mistaken for GraphQL. */
function looksLikeGraphQLQuery(query: string): boolean {
  const s = query.trim();
  return /^(query|mutation|subscription)\b/.test(s) || /^\{\s*[A-Za-z_.]/.test(s);
}

type HeaderPair = { name: string; value: string };
type CookiePair = { name: string; value: string };
type FormField = { name: string; value: string; skippedFile?: boolean };

type ParsedCurl = {
  url: string;
  method?: string;
  headers: HeaderPair[];
  contentType: string;
  bodies: string[];
  jsonBody?: string;
  formFields: FormField[];
  cookies: CookiePair[];
  forceGet: boolean;
  user?: string;
};

function normalizeCurl(input: string): string {
  return input
    .replace(/\\\r?\n/g, ' ')
    .replace(/\^\r?\n/g, ' ')
    .replace(/\r\n/g, '\n')
    .trim();
}

function tokenize(command: string): string[] {
  const tokens: string[] = [];
  let i = 0;

  while (i < command.length) {
    while (i < command.length && /\s/.test(command[i])) i += 1;
    if (i >= command.length) break;

    const quote = command[i];
    if (quote === "'" || quote === '"') {
      i += 1;
      let token = '';
      while (i < command.length && command[i] !== quote) {
        if (quote === '"' && command[i] === '\\' && i + 1 < command.length) {
          token += command[i + 1];
          i += 2;
          continue;
        }
        token += command[i];
        i += 1;
      }
      if (command[i] === quote) i += 1;
      tokens.push(token);
      continue;
    }

    let token = '';
    while (i < command.length && !/\s/.test(command[i])) {
      token += command[i];
      i += 1;
    }
    tokens.push(token);
  }

  return tokens;
}

function parseTokens(tokens: string[], warnings: string[]): ParsedCurl {
  const parsed: ParsedCurl = {
    url: '',
    headers: [],
    contentType: '',
    bodies: [],
    formFields: [],
    cookies: [],
    forceGet: false,
  };

  let index = 0;
  if (tokens[0]?.toLowerCase() === 'curl' || tokens[0]?.toLowerCase() === 'curl.exe') {
    index = 1;
  }

  const takeValue = (flag: string): string => {
    index += 1;
    const value = tokens[index];
    if (value === undefined) {
      throw new CurlParseError(`Flag ${flag} is missing a value.`);
    }
    return value;
  };

  while (index < tokens.length) {
    const token = tokens[index];

    if (!token.startsWith('-')) {
      if (looksLikeUrl(token)) {
        if (parsed.url) warnings.push('Ignored extra URL in cURL command.');
        else parsed.url = token;
      } else if (token.toLowerCase() !== 'curl') {
        warnings.push(`Ignored unexpected argument "${truncate(token)}".`);
      }
      index += 1;
      continue;
    }

    const { flag, attached } = splitFlag(token);

    if (flag === '--url') {
      parsed.url = attached ?? takeValue(flag);
      index += 1;
      continue;
    }
    if (flag === '-X' || flag === '--request') {
      parsed.method = (attached ?? takeValue(flag)).toUpperCase();
      index += 1;
      continue;
    }
    if (flag === '-H' || flag === '--header') {
      const header = parseHeader(attached ?? takeValue(flag));
      if (header) {
        parsed.headers.push(header);
        if (header.name.toLowerCase() === 'content-type') {
          parsed.contentType = header.value;
        }
      }
      index += 1;
      continue;
    }
    if (
      flag === '-d' ||
      flag === '--data' ||
      flag === '--data-raw' ||
      flag === '--data-binary' ||
      flag === '--data-ascii'
    ) {
      const value = attached ?? takeValue(flag);
      if (flag !== '--data-raw' && value.startsWith('@')) {
        warnings.push('Skipped file body (@file is not available in the browser).');
      } else {
        parsed.bodies.push(value);
      }
      index += 1;
      continue;
    }
    if (flag === '--data-urlencode') {
      parsed.bodies.push(attached ?? takeValue(flag));
      if (!parsed.contentType) parsed.contentType = 'application/x-www-form-urlencoded';
      index += 1;
      continue;
    }
    if (flag === '--json') {
      const value = attached ?? takeValue(flag);
      if (value.startsWith('@')) {
        warnings.push('Skipped file body (@file is not available in the browser).');
      } else {
        parsed.jsonBody = value;
        parsed.contentType = 'application/json';
        if (!parsed.method) parsed.method = 'POST';
      }
      index += 1;
      continue;
    }
    if (flag === '-F' || flag === '--form' || flag === '--form-string') {
      const field = parseFormField(attached ?? takeValue(flag), flag === '--form-string');
      parsed.formFields.push(field);
      if (!parsed.contentType) parsed.contentType = 'multipart/form-data';
      index += 1;
      continue;
    }
    if (flag === '-b' || flag === '--cookie') {
      const value = attached ?? takeValue(flag);
      if (value.startsWith('@')) {
        warnings.push('Skipped cookie file (@file is not available in the browser).');
      } else {
        parseCookieString(value).forEach((cookie) => parsed.cookies.push(cookie));
      }
      index += 1;
      continue;
    }
    if (flag === '-A' || flag === '--user-agent') {
      parsed.headers.push({ name: 'User-Agent', value: attached ?? takeValue(flag) });
      index += 1;
      continue;
    }
    if (flag === '-u' || flag === '--user') {
      parsed.user = attached ?? takeValue(flag);
      index += 1;
      continue;
    }
    if (flag === '-G' || flag === '--get') {
      parsed.forceGet = true;
      parsed.method = 'GET';
      index += 1;
      continue;
    }
    if (flag === '-e' || flag === '--referer') {
      parsed.headers.push({ name: 'Referer', value: attached ?? takeValue(flag) });
      index += 1;
      continue;
    }

    if (flag === '-I' || flag === '--head') {
      parsed.method = 'HEAD';
      index += 1;
      continue;
    }

    const warnMessage = WARN_FLAGS[flag];
    if (warnMessage) {
      warnings.push(warnMessage);
      if (
        VALUE_FLAGS.has(flag) &&
        attached === undefined &&
        tokens[index + 1] &&
        !tokens[index + 1].startsWith('-')
      ) {
        index += 1;
      }
      index += 1;
      continue;
    }

    if (SILENT_FLAGS.has(flag) || isSilentShortCluster(flag)) {
      if (flag.includes('k')) warnings.push(WARN_FLAGS['-k']);
      if (flag.includes('L') && !flag.startsWith('--')) warnings.push(WARN_FLAGS['-L']);
      index += 1;
      continue;
    }

    warnings.push(`Ignored unsupported flag ${flag}.`);
    if (
      VALUE_FLAGS.has(flag) &&
      attached === undefined &&
      tokens[index + 1] &&
      !looksLikeUrl(tokens[index + 1]) &&
      !tokens[index + 1].startsWith('-')
    ) {
      index += 1;
    }
    index += 1;
  }

  return parsed;
}

function splitFlag(token: string): { flag: string; attached?: string } {
  if (token.startsWith('--')) {
    const eq = token.indexOf('=');
    if (eq > 2) return { flag: token.slice(0, eq), attached: token.slice(eq + 1) };
    return { flag: token };
  }
  if (token.startsWith('-X') && token.length > 2 && token !== '-X') {
    return { flag: '-X', attached: token.slice(2) };
  }
  if (token.startsWith('-H') && token.length > 2 && token !== '-H') {
    return { flag: '-H', attached: token.slice(2) };
  }
  if (token.startsWith('-d') && token.length > 2 && token !== '-d') {
    return { flag: '-d', attached: token.slice(2) };
  }
  return { flag: token };
}

function looksLikeUrl(value: string): boolean {
  return (
    /^(https?:\/\/|www\.)/i.test(value) ||
    value.startsWith('/') ||
    /^[a-z0-9.-]+\.[a-z]{2,}/i.test(value)
  );
}

function ensureAbsoluteUrl(value: string, baseUrl?: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith('//')) return `https:${value}`;
  if (value.startsWith('/') && baseUrl) {
    try {
      return new URL(value, baseUrl).toString();
    } catch {
      return `${baseUrl.replace(/\/$/, '')}${value}`;
    }
  }
  return `https://${value}`;
}

function parseHeader(raw: string): HeaderPair | null {
  const index = raw.indexOf(':');
  if (index <= 0) return null;
  return { name: raw.slice(0, index).trim(), value: raw.slice(index + 1).trim() };
}

function parseCookieString(raw: string): CookiePair[] {
  if (raw.startsWith('@')) return [];
  return raw
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const eq = part.indexOf('=');
      if (eq <= 0) return { name: part, value: '' };
      return { name: part.slice(0, eq).trim(), value: part.slice(eq + 1).trim() };
    })
    .filter((cookie) => cookie.name);
}

function parseFormField(raw: string, asString: boolean): FormField {
  const eq = raw.indexOf('=');
  if (eq <= 0) return { name: raw, value: '' };
  const name = raw.slice(0, eq).trim();
  const value = raw.slice(eq + 1);
  if (!asString && value.startsWith('@')) {
    return { name, value: '', skippedFile: true };
  }
  return { name, value };
}

function isSilentShortCluster(flag: string): boolean {
  if (!/^-[a-zA-Z]+$/.test(flag) || flag.startsWith('--')) return false;
  return [...flag.slice(1)].every((char) => 'sSfi#NvLkg'.includes(char));
}

function resolveMethod(parsed: ParsedCurl): string {
  if (parsed.method) return parsed.method.toUpperCase();
  if (parsed.jsonBody || parsed.bodies.length > 0 || parsed.formFields.length > 0) return 'POST';
  return 'GET';
}

function resolveBodyKind(parsed: ParsedCurl, contentType: string): 'json' | 'form' | 'raw' | 'none' {
  if (parsed.forceGet) return 'none';
  if (parsed.jsonBody) return 'json';
  if (parsed.formFields.length > 0 || contentType.includes('multipart/form-data')) return 'form';
  if (contentType.includes('application/x-www-form-urlencoded')) return 'form';
  if (parsed.bodies.length === 0) return 'none';
  if (contentType.includes('application/json') || looksLikeJson(parsed.bodies.join(''))) return 'json';
  if (looksLikeUrlEncoded(parsed.bodies.join('&'))) return 'form';
  return 'raw';
}

function looksLikeJson(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

function looksLikeUrlEncoded(value: string): boolean {
  if (!value.includes('=')) return false;
  return /^[^=&\s]+=/.test(value) && !value.trim().startsWith('{');
}

function appendUrlEncoded(
  raw: string,
  location: 'query' | 'form',
  addParam: (param: IParameter) => void,
) {
  const params = new URLSearchParams(raw);
  params.forEach((value, name) => {
    const inferred = inferScalar(value);
    addParam(scalarParam(name, location, inferred.type, inferred.value, true));
  });
}

function addJsonBody(
  raw: string,
  addParam: (param: IParameter) => void,
  warnings: string[],
) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    warnings.push('Request body was not valid JSON and was imported as raw body.');
    addParam({
      Name: 'body',
      Type: 'string',
      Description: '',
      Required: true,
      Location: 'raw_body',
      DefaultValue: raw,
      ContentType: 'application/json',
    });
    return;
  }

  const result = jsonToParameters(parsed, { location: 'body', rootName: 'body' });
  warnings.push(...result.warnings);
  Object.values(result.parameters).forEach(addParam);
}

function scalarParam(
  name: string,
  location: string,
  type: string,
  defaultValue: unknown,
  required: boolean,
): IParameter {
  return {
    Name: name,
    Type: type,
    Description: '',
    Required: required,
    Location: location,
    DefaultValue: defaultValue,
  };
}

function inferScalar(value: string): { type: string; value: unknown } {
  if (value === 'true') return { type: 'boolean', value: true };
  if (value === 'false') return { type: 'boolean', value: false };
  if (/^-?\d+$/.test(value)) return { type: 'integer', value: Number(value) };
  if (/^-?\d+\.\d+$/.test(value)) return { type: 'number', value: Number(value) };
  return { type: 'string', value };
}

function relativizePath(fullUrl: URL, baseUrl?: string): { path: string; warning?: string } {
  const path = fullUrl.pathname || '/';
  if (!baseUrl) return { path: path || '/' };

  try {
    const base = new URL(baseUrl);
    if (base.host !== fullUrl.host || base.protocol !== fullUrl.protocol) {
      return {
        path,
        warning: `cURL host (${fullUrl.origin}) differs from the tool base URL (${base.origin}). Path was taken from the cURL URL.`,
      };
    }
    const basePath = base.pathname.replace(/\/$/, '');
    if (basePath && (path === basePath || path.startsWith(`${basePath}/`))) {
      return { path: path.slice(basePath.length) || '/' };
    }
    return { path };
  } catch {
    return { path };
  }
}

function extractPathParams(pathname: string): { path: string; params: string[] } {
  const params: string[] = [];
  const path = pathname.replace(/:([A-Za-z_][\w]*)/g, (_match, name: string) => {
    params.push(name);
    return `{${name}}`;
  });
  for (const match of path.matchAll(/\{([^}]+)\}/g)) {
    if (!params.includes(match[1])) params.push(match[1]);
  }
  return { path: path || '/', params };
}

function deriveName(path: string): string {
  const segments = path.split('/').filter((segment) => segment && !segment.startsWith('{'));
  const raw = (segments[segments.length - 1] || 'imported_api')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, MAX_NAME_LENGTH);
  return raw || 'imported_api';
}

function uniqueKey(existing: Record<string, IParameter>, name: string): string {
  if (!existing[name]) return name;
  let suffix = 2;
  while (existing[`${name}_${suffix}`]) suffix += 1;
  return `${name}_${suffix}`;
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function truncate(value: string, max = 40): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}
